/*
 * Copyright (C) 2019 - present by Potentially
 *
 * Please see distribution for license.
 */

import {
  LabelFormat,
  ScoredChartType,
} from '@app/app/page-modules/diagnostics/components/view/competency-scale/competency-scale.component';
import { UserSearch } from '@app/app/shared/models/admin/members.model';
import { DiagnosticFormattedQuestion, DiagnosticLogicIcon, EditorContent, QuestionResponse, ResourceTag } from '..';

export type ReportSectionTypes =
  | 'STANDARD_SECTION'
  | 'CONDITIONAL_SECTION'
  | 'RESOURCE_SECTION'
  | 'STANDARD_SECTION_CHART'
  | 'FEEDBACK_STATS_SECTION'
  | 'FEEDBACK_QUESTIONS_SECTION'
  | 'FEEDBACK_PROVIDERS_SECTION';

export enum FeedbackSectionType {
  FEEDBACK_STATS_SECTION = 'FEEDBACK_STATS_SECTION',
  FEEDBACK_QUESTIONS_SECTION = 'FEEDBACK_QUESTIONS_SECTION',
  FEEDBACK_PROVIDERS_SECTION = 'FEEDBACK_PROVIDERS_SECTION',
}

export const FEEDBACK_SNIPPETS: Record<FeedbackSectionType, string> = {
  [FeedbackSectionType.FEEDBACK_STATS_SECTION]: 'translations.diagnostics.labels.feedbackStats',
  [FeedbackSectionType.FEEDBACK_QUESTIONS_SECTION]: 'translations.diagnostics.labels.feedbackQuestions',
  [FeedbackSectionType.FEEDBACK_PROVIDERS_SECTION]: 'translations.diagnostics.labels.feedbackProviders',
};

export interface ReportSectionItem {
  title: string;
  description?: string;
  image: string;
  type: ReportSectionTypes;
}

export interface CreateReportStandardChartSectionRequest {
  type: ReportSectionTypes;
}

export interface CreateReportStandardSectionRequest {
  type: ReportSectionTypes;
  competencyLevelWrapper?: ReportCompetencyLevelWrapper;
  dynamicContent: EditorContent[];
}

export interface CreateReportConditionalSectionRequest {
  type: ReportSectionTypes;
  tagUid: string;
  showTitle: boolean;
  competencyLevelWrapper?: ReportCompetencyLevelWrapper;
  dynamicContent: EditorContent[];
  // Sent on a 360 only, which draws this section as a scored scale rather than a graph per band.
  labelFormat?: LabelFormat;
  chartType?: ScoredChartType;
}

export interface CreateReportResourceSectionRequest {
  type: ReportSectionTypes;
  tagsForResourceSection?: string[];
}

export interface CreateReportFeedbackSectionRequest {
  type: ReportSectionTypes;
  title?: string;
}

export interface UpdateReportSectionResponse {
  type: ReportSectionTypes;
  uid: string;
  dynamicContent?: EditorContent[];
  showTitle?: boolean;
  learningGoalWrapper: {
    tagUid: string;
    dynamicContent: EditorContent[];
    competencyLevels?: ReportCompetencyLevelWrapper[];

    // front-end specific
    levelUidToUpdate?: string;
  };
}

export interface UpdateReportStandardSectionRequest {
  dynamicContent: EditorContent[];
}

export interface UpdateReportResourceSectionRequest {
  tags?: string[];
}

export interface UpdateReportFeedbackQuestionRequest {
  includedFeedbackQuestions: { included: boolean; questionUid: string }[];
  // Optional custom title for the feedback questions section
  title?: string;
}

export interface UpdateReportFeedbackStatsRequest {
  stats: { enabled: boolean; type: FeedbackStatsType }[];
  // Optional custom title for the feedback stats section
  title?: string;
}

export interface UpdateReportFeedbackProvidersRequest {
  // Optional custom title for the feedback providers section
  title?: string;
}

export interface UpdateReportConditionalSectionRequest {
  tagUid: string;
  showTitle: boolean;
  showLevels: boolean;
  labelFormat: LabelFormat;
  chartType: ScoredChartType;
  competencyLevelWrapper: ReportCompetencyLevelWrapper;
  dynamicContent: EditorContent[];
}

export interface Report {
  title: UpdateReportTitleRequest[];
  sections: ReportSection[];
}

export interface ReportSection {
  type: ReportSectionTypes;
  title?: string;
  dynamicContent: EditorContent[];

  uid?: string;
  showTitle?: boolean;
  showLevels?: boolean;
  showIcons?: boolean;
  showPercentage?: boolean;
  labelFormat?: LabelFormat;
  chartType?: ScoredChartType;

  learningGoalWrapper?: ReportSectionLearningGoalWrapper;

  // front-end specific
  tagTitle?: string;
  isHidden?: boolean;
}

export interface ReportResourceSection extends ReportSection {
  tags?: ResourceTag[];
  tagsForResourceSection?: string[];
}

export interface ReportFeedbackQuestionsSection extends ReportSection {
  includedQuestions: IncludedFeedbackQuestion[];
  responses: ReportFeedbackQuestionsResponses[];
}

export interface ReportFeedbackStatsSection extends ReportSection {
  feedbackStats: FeedbackStats[];
}

export interface IncludedFeedbackQuestion {
  included: boolean;
  question: DiagnosticFormattedQuestion;
}

export interface ReportFeedbackQuestionsResponses {
  feedbackProvider: FeedbackScoresProvider;
  responses: [];
}

export interface ReportSectionLearningGoalWrapper {
  tagUid: string;
  dynamicContent: EditorContent[];
  competencyLevelWrapper?: ReportCompetencyLevelWrapper;

  // front-end specific
  competencyLevels?: ReportCompetencyLevelWrapper[];
}

export interface UpdateReportTitleRequest {
  languageCode: string;
  value: string;
}

export interface ReportCompetencyLevelWrapper {
  levelUid: string;
  showGraph: boolean;
  graphType: CompetencyLevelGraphType;
  labelFormat: LabelFormat;
  dynamicContent: EditorContent[];

  // front-end specific
  competencyTitle?: string;
}

export interface GeneratedReport {
  assessmentUid: string;
  completedOn: string;
  showTitle: boolean;
  showLogo: boolean;
  userUid: string;
  title: UpdateReportTitleRequest;
  sections: GeneratedReportSection[];
  allFeedbackProviders?: FeedbackProviderTagScore[];
  reportMode?: ReportMode;
}

export interface GeneratedReportStandardSection {
  type: 'STANDARD_SECTION';
  uid: string;
  title?: string;
  dynamicContent: EditorContent[];
}

export interface GeneratedReportConditionalSection {
  type: 'CONDITIONAL_SECTION';
  uid: string;
  title?: string;
  showTitle: boolean;
  showLevels: boolean;
  labelFormat: LabelFormat;
  learningGoalWrapper: LearningGoalWrapper;
  chartType?: ScoredChartType;

  // front-end specific
  chartColor?: string;
  trimmedFromStart?: boolean;
  hideCompetency?: boolean;
}

export interface GeneratedReportResourceSection {
  type: 'RESOURCE_SECTION';
  title?: string;
  uid: string;
  tags: ResourceTag[];
}

export interface ScoredFeedbackTag {
  tagUid: string;
  title: string;
  userScore: number;
  total: number;
  children: ScoredFeedbackTag[];
  feedbackProviderTagScores: FeedbackProviderTagScore[];
  avgFeedbackScore?: number;
  overallAverageScore?: number;
  feedbackScores?: number[];
}

export interface FeedbackProviderTagScore {
  providerImage?: string;
  providerName?: string;
  providerUid?: string;
  tagScore?: number;
}

export interface FeedbackScoresProvider {
  userUid: string;
  name: string;
  email: string;
  jobTitle?: string;
  image?: string;
  organization?: string;
  relationship?: string;
  observationPeriod?: string;
  observationStrength?: string;
  profileLink?: string;
  feedbackProvidedOn?: Date;

  // frontend specific
  isEnabled?: boolean;
}

export interface GeneratedReportFeedbackStatsSection {
  type: 'FEEDBACK_STATS_SECTION';
  uid: string;
  title?: string;
  feedbackStats: FeedbackStats[];
}

export interface FeedbackStats {
  type: FeedbackStatsType;
  avgSelectedFeedbackScores?: number;
  avgUserScoreFromAllAttempts?: number;
  avgUserScoreFromLatestAttempt?: number;
  avgSelectedProvidersScores?: number[];
  providersConsensus?: number;
  userVsProvidersConsensus?: number;
  scoreChange?: number;
  percentageChange?: number;
  since?: Date;
  enabled?: boolean;
  totalSelfAssessments?: number;
  userPercentile?: number;
}

export type FeedbackStatsType =
  | 'AVG_SCORE'
  | 'AVG_RATING'
  | 'CONSENSUS'
  | 'DISTANCE_TRAVELLED'
  | 'WORKSPACE_BENCHMARK'
  | 'NATIONAL_BENCHMARK';

export interface GeneratedReportFeedbackProvidersSection {
  type: 'FEEDBACK_PROVIDERS_SECTION';
  uid: string;
  title?: string;
  feedbackProviders: FeedbackScoresProvider[];
}

export interface GeneratedReportFeedbackQuestionsSection {
  type: 'FEEDBACK_QUESTIONS_SECTION';
  uid: string;
  title?: string;
  enrichedAnswers: FeedbackEnrichedAnswer[];
  includedQuestions: IncludedFeedbackQuestion[];
  responses: ReportFeedbackQuestionsResponses[];
}

export interface FeedbackEnrichedAnswer {
  questionUid: string;
  type: FeedbackEnrichedAnswerType;
  numberOfFeedbackResponses: number;
  optionScores?: FeedbackEnrichedAnswerScore[];
  answers?: FeedbackEnrichedAnswerText[];
  sliderScores?: SliderQuestionScore[];
  learnerQuestionAnswer?: QuestionResponse;
}

export interface FeedbackEnrichedAnswerScore {
  optionUid: string;
  optionLabel: string;
  averageScoreAcrossFeedbacks: number;
  selectedCount: number;
  feedbackProviders: FeedbackProviderSummary[];
}

export interface FeedbackProviderSummary {
  providerUid: string;
  name: string;
  imageUrl?: string;
}

export interface FeedbackEnrichedAnswerText {
  providerImage: string;
  providerName: string;
  richText: EditorContent[];
}

export interface SliderQuestionScore {
  providerName: string;
  providerImage: string;
  score: number;
}

export type FeedbackEnrichedAnswerType = 'CHOICE_QUESTION' | 'TEXT_INPUT_QUESTION' | 'INFO_QUESTION' | 'SLIDER_QUESTION';

export type GeneratedReportSection =
  | GeneratedReportStandardSection
  | GeneratedReportConditionalSection
  | GeneratedReportResourceSection
  | GeneratedReportFeedbackStatsSection
  | GeneratedReportFeedbackProvidersSection
  | GeneratedReportFeedbackQuestionsSection;

export type GeneratedReportSectionType =
  | 'STANDARD_SECTION'
  | 'CONDITIONAL_SECTION'
  | 'RESOURCE_SECTION'
  | 'FEEDBACK_QUESTIONS_SECTION'
  | 'FEEDBACK_STATS_SECTION'
  | 'FEEDBACK_PROVIDERS_SECTION';

export type ReportMode = 'FULL' | 'LITE';

export interface LearningGoalWrapper {
  dynamicContent: EditorContent[];
  competencyLevels: CompetencyLevel[];
  allCompetencyLevels?: CompetencyLevel[];
  scoredFeedbackTags?: ScoredFeedbackTag[];
  tagTitle: string;
  tagUid: string;
  maxScore: number;
  scored: number;
  icon: DiagnosticLogicIcon;
  minIcon: DiagnosticLogicIcon;
  maxIcon: DiagnosticLogicIcon;
}

export interface CompetencyLevel {
  dynamicContent: EditorContent[];
  levelTitle: UpdateReportTitleRequest[];
  levelUid: string;
  max: number;
  scored: number;
  showGraph: boolean;
  graphType: CompetencyLevelGraphType;
}

export interface ReportReordering {
  sections: string[];
}

export interface ShowReportLogoRequest {
  showLogo: boolean;
  alignment: ShowReportLogoAlignment;
}

export interface ReportSectionIcons {
  icon: DiagnosticLogicIcon;
  minIcon: DiagnosticLogicIcon;
  maxIcon: DiagnosticLogicIcon;
}

export interface MyPagesAssessmentsResponse {
  assessmentUid: string;
  thumbnail: string | null;
  title: string;
  assessmentEvents: AssessmentEvent[];
  formattedUri: string;
  authorDetails: AssessmentAuthorDetails;
  canResume?: boolean;
  hasCompletedAssessment?: boolean;
  hasFeedback?: boolean;
  retake?: boolean;
}

export interface FeedbackRecipientSummaryEntry {
  feedbackRecipient: UserSearch;
  assessments: FeedbackResponseSummary[];
}

export interface FeedbackResponseSummary {
  assessmentCardUid: string;
  assessmentHeadline: string;
  assessmentThumbnail?: string;
  allVersionsCount: number;
  lastCompletedOn?: Date | null;
  completionDatesByVersion: Record<number, Date | null>;
  feedbackStatus: FeedbackStatus;
}

export interface FeedbackStatus {
  selfFeedbackProvided: boolean;
  providerFeedbackCompleted: boolean;
  providerFeedbackStarted: boolean;
}

export interface AssessmentEvent {
  questionResponsesUid: string;
  status: 'COMPLETED' | 'IN_PROGRESS';
  version: number;
  startedOn: string;
  completedOn: string;
}

export interface AssessmentAuthorDetails {
  name: string;
  orgLogoUrl: string;
  organization: string;
  uid: string;
  userImageUrl: string;
}

export type ShowReportLogoAlignment = 'LEFT' | 'RIGHT' | 'CENTER';

export type CompetencyLevelGraphType = 'CIRCLE' | 'BAR' | 'SEGMENTED_BAR' | 'SCALE';

export interface FeedbackRecipient {
  uid: string;
  firstName: string;
  lastName: string;
  imageUrl: string;
}
